import type { ProjectRecord } from '~/models/project'
import type { ProjectId } from '~/types/ids'
import type { Result } from '~/utils/result'
import { err, ok } from '~/utils/result'
import type { SolaDatabase } from '../db'
import { type StorageError, toStorageError } from '../errors'
import { STORE } from '../schema'

/**
 * Projects (D-062).
 *
 * Reads only, at Checkpoint 2.5c. The two write paths that exist both need to
 * write a project alongside other records in one transaction — Project Setup
 * and the schema v2 migration — so neither can go through a `putProject` here
 * without breaking its own atomicity. Project end and the completion summary
 * arrive at 2.5d and will bring their own writer.
 */

export async function getProject(
  db: SolaDatabase,
  id: ProjectId,
): Promise<Result<ProjectRecord | undefined, StorageError>> {
  try {
    return ok(await db.get(STORE.projects, id))
  } catch (cause) {
    return err(toStorageError(cause))
  }
}

/**
 * The project currently running.
 *
 * There is at most one: a project ends only by explicit decision (D-020), and
 * setup is refused while one is active. Should several ever be found, the most
 * recently started wins and the rest are left alone rather than repaired
 * silently — a bug that hides itself is worse than one that shows.
 *
 * **A start date may now be in the future (D-107), and this sort is deliberately
 * not made cleverer for it.** Preferring a project that has already begun over
 * one that has not would pick a silent winner in exactly the corrupt state the
 * paragraph above wants visible. A second row stays unreachable for the reason
 * it always was: `completeProjectSetup` refuses while any active project exists,
 * waiting or running, which is also why waiting is not a `ProjectStatus`.
 */
export async function getActiveProject(
  db: SolaDatabase,
): Promise<Result<ProjectRecord | undefined, StorageError>> {
  try {
    const active = await db.getAllFromIndex(STORE.projects, 'status', 'active')
    const sorted = [...active].sort((left, right) => right.startDate.localeCompare(left.startDate))
    return ok(sorted[0])
  } catch (cause) {
    return err(toStorageError(cause))
  }
}

/** Every project, newest first. Project history outlives the phases inside it. */
export async function listProjects(
  db: SolaDatabase,
): Promise<Result<readonly ProjectRecord[], StorageError>> {
  try {
    const all = await db.getAll(STORE.projects)
    return ok([...all].sort((left, right) => right.startDate.localeCompare(left.startDate)))
  } catch (cause) {
    return err(toStorageError(cause))
  }
}
